import { useCallback, useMemo, useRef } from 'react'
import { ProductReferenceFragment } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import { ProductList } from './product-list'
import {
  useQueryState,
  useScrollPosition,
  useWindowSize,
} from '@dustin-web/microsite-components/src/hooks'
import { DigitalDataOrigin } from '../utils/digital-data-types'
import InfinityScrollButton from '@dustin-web/microsite-components/src/infinity-scroll-button'
import { ProductCardCampaign } from '../cards'
import useCleanRouter from '@dustin-web/microsite-components/src/hooks/use-clean-router'
import { flatten } from '@dustin-web/microsite-data/src/utils'
import { useProductListSettings } from './use-product-list-settings'
import type { Props as HeadingProps } from '@dustin-web/microsite-components/src/heading'
import { getRefVar } from '@dustin-web/microsite-components/src/hooks/vars'

type PromotedProduct = {
  imageUrl?: string
  campaignText?: string
}

type Props = {
  productReferences: ProductReferenceFragment[][]
  digitalDataOrigin: DigitalDataOrigin
  promotedProduct?: (ProductReferenceFragment['searchResultProduct'] & PromotedProduct) | null
  searchPhrase?: string
  isLoadingMore: boolean
  totalHits: number
  handleLoadMore: () => void
  showSideButtons?: boolean
  page: number
  viewTypeOverride?: string
  hTag?: HeadingProps['hTag']
  categoryId?: string | undefined
}

export const ProductInfinityList = ({
  categoryId,
  productReferences,
  digitalDataOrigin,
  promotedProduct,
  searchPhrase,
  isLoadingMore,
  totalHits,
  handleLoadMore,
  showSideButtons,
  page,
  viewTypeOverride,
  hTag,
}: Props) => {
  const { setQueryState } = useQueryState()
  const { height } = useWindowSize()
  const { pageSize } = useProductListSettings()
  const infinityScrollButtonContainerRef = useRef<HTMLDivElement | null>(null)
  const { pageUriWithoutQuery } = useCleanRouter()

  const nextPage = page + 1
  const nextPagePath = `${pageUriWithoutQuery}${`?page=${nextPage}`}`

  const products = useMemo(() => flatten(productReferences), [productReferences])

  const totalPages = useMemo(() => Math.ceil(totalHits / pageSize), [totalHits])
  const ensureServerSideNavigation = page - productReferences.length >= 9

  const shouldInfiniteLoad = useCallback(
    (currPos: { x: number; y: number }) => {
      const screenHeight = height || 0
      const position = currPos.y - screenHeight * 2

      const shouldLoad =
        !ensureServerSideNavigation &&
        totalPages &&
        page <= totalPages &&
        position < 0 &&
        !isLoadingMore &&
        page % 3 !== 0

      return shouldLoad
    },
    [height, page, isLoadingMore]
  )

  useScrollPosition(
    ({ currPos }) => {
      if (shouldInfiniteLoad(currPos)) {
        handleLoadMore()
      }
    },
    [page, isLoadingMore],
    infinityScrollButtonContainerRef,
    false,
    10
  )

  const handleLoadMoreClick = () => {
    setQueryState(
      {
        page: nextPage.toString(),
      },
      false
    )

    handleLoadMore()
    const storedRef = getRefVar()
    if (storedRef?.current) {
      storedRef.current.tabIndex = -1
      storedRef.current.focus()
    }
  }

  return (
    <>
      <ProductList
        categoryId={categoryId}
        productReferences={products}
        loading={isLoadingMore}
        infinityScroll={true}
        digitalDataOrigin={digitalDataOrigin}
        searchPhrase={searchPhrase}
        showSideButtons={showSideButtons}
        viewTypeOverride={viewTypeOverride}
        hTag={hTag}
        promotedProductCard={
          promotedProduct ? (
            <ProductCardCampaign
              product={promotedProduct}
              viewType={viewTypeOverride ?? 'gallery'}
              ticket={''}
              digitalDataOrigin={'CampaignPage'}
              imagePriority={true}
            />
          ) : null
        }
      />

      <div className="u-mb-96" ref={infinityScrollButtonContainerRef}>
        {!isLoadingMore && totalPages >= page && products.length > 0 && (
          <InfinityScrollButton
            currentlyViewing={products.length}
            maxViewing={totalHits}
            loading={false}
            nextPagePath={nextPagePath}
            ensureServerSideNavigation={ensureServerSideNavigation}
            onClick={handleLoadMoreClick}
          />
        )}
      </div>
    </>
  )
}
